import React from 'react';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/QuickSetup.module.scss';
import { Icon, Input } from 'Components';
import RadioCard, {
	RadioCardOption
} from 'LearningManager/components/RadioCard';
import { useStepper } from 'LearningManager/hooks/Stepper.context';

const certOptions: RadioCardOption[] = [
	{ value: 'one-year', label: 'One year' },
	{
		value: 'custom',
		label: 'Custom expiration',
		showDatePicker: true,
		datePlaceholder: 'Enter the deadline date'
	},
	{ value: 'never', label: 'Never expires' }
];

const startOptions: RadioCardOption[] = [
	{ value: 'immediate', label: 'Immediate' },
	{
		value: 'schedule',
		label: 'Schedule',
		showDatePicker: true,
		datePlaceholder: 'Enter the start date'
	}
];

const dueOptions: RadioCardOption[] = [
	{ value: 'none', label: 'None' },
	{
		value: 'custom',
		label: 'Custom expiration',
		showDatePicker: true,
		datePlaceholder: 'Enter the deadline date'
	}
];

const AssignTypeOptions: RadioCardOption[] = [
	{
		value: 'one-time-assignment',
		label: 'One-Time Assignment',
		subLabel: 'Assign the course only to the selected users'
	},
	{
		value: 'ongoing-assignment',
		label: 'Ongoing Assignment',
		subLabel:
			'Automatically assign this course to new users who join later and meet the assignment criteria'
	}
];

const Configure: React.FC = () => {
	const {
		draft: {
			configure: { assignmentName }
		},
		updateConfigure
	} = useStepper();
	return (
		<div className={styles.configure}>
			<Heading
				title="Name"
				subHeading="This name helps organize and classify this assignment."
			/>
			<Input
				className={styles.configure__input}
				value={assignmentName}
				onChange={(e) =>
					updateConfigure({ assignmentName: e.target.value })
				}
				placeholder="Enter assignment name"
			/>
			<Scheduling />
			<AssignmentRules />
		</div>
	);
};

const Scheduling = () => {
	const {
		draft: {
			configure: { assignmentStart, certDuration, dueDate }
		},
		updateConfigure
	} = useStepper();

	const onChange = (name: 'certDuration' | 'assignmentStart' | 'dueDate') => (
		value: string
	) => {
		updateConfigure({ [name]: value });
	};

	return (
		<div className={styles.configure__wrapper}>
			<Heading
				title="Scheduling & Deadlines"
				subHeading="Set important dates for this training assignment"
			/>
			<div className={styles.configure__formContainer}>
				<RadioCard
					title="Certification Duration"
					icon={<Icon type="durationIcon" role="img" />}
					tooltip="Set how long certification remains valid after completion"
					options={certOptions}
					value={certDuration}
					onChange={onChange('certDuration')}
				/>
				<RadioCard
					title="Assignment Start"
					icon={<Icon type="startIcon" role="img" />}
					tooltip="Choose when this assignment becomes available"
					options={startOptions}
					value={assignmentStart}
					onChange={onChange('assignmentStart')}
				/>
				<RadioCard
					title="Due date"
					icon={<Icon type="dueDateIcon" role="img" />}
					tooltip="Set the deadline for completion"
					options={dueOptions}
					value={dueDate}
					onChange={onChange('dueDate')}
				/>
			</div>
		</div>
	);
};

const AssignmentRules: React.FC = () => {
	const {
		draft: {
			configure: { assignmentType }
		},
		updateConfigure
	} = useStepper();

	return (
		<div className={styles.configure__wrapper}>
			<Heading
				title="Assignment Rules"
				subHeading="Choose how this assignment applies to future users"
			/>
			<RadioCard
				title="Assignment Type"
				icon={<Icon type="assignTypeIcon" role="img" />}
				tooltip="Choose how this course will be assigned to your team"
				options={AssignTypeOptions}
				onChange={(value) => updateConfigure({ assignmentType: value })}
				value={assignmentType}
			/>
		</div>
	);
};

export default Configure;
